import { useFieldArray, useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Product, Sale, SaleItem, SaleItemRequest, SaleRequest } from "../../../../service/model/store.model";
import { Box, Button, Dialog, DialogTitle, Grid } from "@mui/material";
import FormField from "../../../../components/form-controls/form-field";
import theme from "../../../../app/app-theme";
import useMembers from "../../../../hooks/use-members";
import { useEffect, useState } from "react";
import SaleItemTable from "./sale-items-table";
import SaleItemForm from "./sale-item-form";
import useProducts from "../../../../hooks/use-products";
import { AutocompleteInput } from "../../../../components/form-input-field-wrappers/autocomplete-dropdown-input";
import FormCloseButton from "../../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import CreateButton from "../../../../components/create-button";

interface SaleFormProps {
    readonly sale?: Sale
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: SaleRequest) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: 400,
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const SaleForm: React.FC<SaleFormProps> = ({ sale, editable, onClose, onSubmit }) => {
    const [members] = useMembers();
    const [products] = useProducts();
    // const [memberId, setMemberId] = useState<number | undefined>(undefined);
    const [showSaleItemForm, setShowSaleItemForm] = useState(false);
    // const [saleItems, setSaleItems] = useState<SaleItem[]>(sale?.saleItems ?? []);
    // console.log("🚀 ~ SaleForm ~ saleItems:", saleItems)

    const schema = yup.object().shape({
        saleDate: yup.string()
            .required(),
        totalAmount: yup.number().required(),
        memberId: yup.number().nullable(),
        saleItems: yup.array().of(yup.object()).min(1, 'At least one sale item is required'),
    });

    
    const calculateTotalAmount = (items: SaleItem[]): number => {
        return items.reduce((total, item) => total + item.totalAmount, 0);
    }

    const { register, control, setValue, watch, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            memberId: sale?.memberId ?? null,
            saleDate: sale?.saleDate ?? new Date().toISOString().slice(0, 10),
            totalAmount: sale ? calculateTotalAmount(sale.saleItems) : 0,
            saleItems: sale?.saleItems ?? [],
        }
    });

    const saleItemList = useFieldArray({
        control,
        name: "saleItems"
    });

    const saleItems = watch("saleItems") as SaleItem[];
    const memberId = watch("memberId");
    const totalAmount = watch("totalAmount");

    useEffect(() => {
        setValue("totalAmount", calculateTotalAmount(saleItems));
    }, [saleItems]);

    const onSubmitSaleHandler = (data: any) => {
        data.memberId = memberId ?? undefined;
        data.totalAmount = saleItems.reduce((sum, item) => sum + item.totalAmount, 0);
        data.saleItems = saleItems.map(item => ({
            id: item.id,
            saleId: sale ? sale.id : undefined,
            productBatchId: item.productBatch.id,
            productId: item.product.id,
            quantity: item.quantity,
            costPrice: item.costPrice,
            sellingPrice: item.sellingPrice,
            totalAmount: item.quantity * item.sellingPrice,
        }));
        onSubmit(data)
    };

    const handleSubmitSaleItem = (data: SaleItemRequest) => {
        const product = (products as Product[]).find(p => p.id === data.productId)!;
        saleItemList.append({
            id: data.id,
            product: product,
            productBatch: product.batches.find(b => b.id === data.productBatchId)!,
            quantity: data.quantity,
            costPrice: data.costPrice,
            sellingPrice: data.sellingPrice,
            totalAmount: data.sellingPrice * data.quantity,
        });
        setShowSaleItemForm(false);
    };

       const handleEditSaleItem = (data: SaleItemRequest) => {
        const product = (products as Product[]).find(p => p.id === data.productId)!;
        const index = saleItems.findIndex(item => item.id === data.id);
        saleItemList.update(index, {
            id: data.id,
            product: product,
            productBatch: product.batches.find(b => b.id === data.productBatchId)!,
            quantity: data.quantity,
            costPrice: data.costPrice,
            sellingPrice: data.sellingPrice,
            totalAmount: data.sellingPrice * data.quantity,
        });
        // setSaleItems(prevItems => [...prevItems, {
        //     id: data.productBatchId,
        //     product: product,
        //     productBatch: product.batches.find(b => b.id === data.productBatchId)!,
        //     quantity: data.quantity,
        //     costPrice: data.costPrice,
        //     sellingPrice: data.sellingPrice,
        //     totalAmount: data.sellingPrice * data.quantity,
        // }]);
        setShowSaleItemForm(false);
    };

    const handleDeleteSaleItem = (id: number) => {
        const index = saleItems.findIndex(item => item.id === id);
        saleItemList.remove(index);
    };

    return (
        <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth="lg">
            <DialogTitle>Sale Details</DialogTitle>
            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitSaleHandler)}>
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                            <FormField>
                                <AutocompleteInput
                                    id="memberId"
                                    name="memberId"
                                    control={control}
                                    label="Select member"
                                    options={members?.map(m => ({ id: m.id, label: `${m.firstName} ${m.lastName}` })) ?? []}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="saleDate"
                                    label="Sale Date"
                                    name="saleDate"
                                    type="date"
                                    // defaultValue={sale?.saleDate ?? new Date().toISOString().slice(0, 10)}
                                    errorMessage={errors.saleDate?.message}
                                    register={register}
                                />
                            </FormField>
                            {/* <FormField>
                                <TextInput
                                    id="totalAmount"
                                    label="Total Amount"
                                    type="number"
                                    disabled={true}
                                    // defaultValue={sale?.totalAmount ?? 0}
                                    error={errors.totalAmount !== undefined}
                                    helperText={errors.totalAmount?.message}
                                    register={register("totalAmount")}
                                />
                            </FormField> */}
                        </Grid>
                        {editable &&
                            <Box sx={{ mb: "12px" }}>
                                <CreateButton variant="contained" onClick={() => setShowSaleItemForm(true)} style={{ marginBottom: "12px" }}>Add Item</CreateButton>
                            </Box>
                        }
                        <SaleItemTable editable={editable} saleItems={saleItems} onEdit={handleEditSaleItem} onDelete={handleDeleteSaleItem}/>
                        <p>Total Amount: <span>{totalAmount}</span> LKR</p>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1, pt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained" >Submit</Button>
                        </Grid>
                    </form>
                    {showSaleItemForm &&
                        <SaleItemForm saleItem={undefined} editable={true} onClose={() => setShowSaleItemForm(false)} onSubmit={handleSubmitSaleItem} />
                    }
                </>
            </Box>
        </Dialog>
    );
}

export default SaleForm;